import _slicedToArray from "C:\\xampp\\htdocs\\beezer\\Platform\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/slicedToArray";
import { useEffect, useState } from 'react';
import { Annotation, EditorState, StateEffect } from '@codemirror/state';
import { indentWithTab } from '@codemirror/commands';
import { EditorView, keymap, placeholder } from '@codemirror/view';
import { basicSetup } from '@uiw/codemirror-extensions-basic-setup';
import { oneDark } from '@codemirror/theme-one-dark';
import { getStatistics } from './utils';
var External = Annotation.define();
export function useCodeMirror(props) {
  var value = props.value,
      selection = props.selection,
      onChange = props.onChange,
      onStatistics = props.onStatistics,
      onCreateEditor = props.onCreateEditor,
      onUpdate = props.onUpdate,
      _props$extensions = props.extensions,
      extensions = _props$extensions === void 0 ? [] : _props$extensions,
      autoFocus = props.autoFocus,
      _props$theme = props.theme,
      theme = _props$theme === void 0 ? 'light' : _props$theme,
      _props$height = props.height,
      height = _props$height === void 0 ? '' : _props$height,
      _props$minHeight = props.minHeight,
      minHeight = _props$minHeight === void 0 ? '' : _props$minHeight,
      _props$maxHeight = props.maxHeight,
      maxHeight = _props$maxHeight === void 0 ? '' : _props$maxHeight,
      _props$placeholder = props.placeholder,
      placeholderStr = _props$placeholder === void 0 ? '' : _props$placeholder,
      _props$width = props.width,
      width = _props$width === void 0 ? '' : _props$width,
      _props$minWidth = props.minWidth,
      minWidth = _props$minWidth === void 0 ? '' : _props$minWidth,
      _props$maxWidth = props.maxWidth,
      maxWidth = _props$maxWidth === void 0 ? '' : _props$maxWidth,
      _props$editable = props.editable,
      editable = _props$editable === void 0 ? true : _props$editable,
      _props$readOnly = props.readOnly,
      readOnly = _props$readOnly === void 0 ? false : _props$readOnly,
      _props$indentWithTab = props.indentWithTab,
      defaultIndentWithTab = _props$indentWithTab === void 0 ? true : _props$indentWithTab,
      _props$basicSetup = props.basicSetup,
      defaultBasicSetup = _props$basicSetup === void 0 ? true : _props$basicSetup,
      root = props.root,
      initialState = props.initialState;

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      container = _useState2[0],
      setContainer = _useState2[1];

  var _useState3 = useState(),
      _useState4 = _slicedToArray(_useState3, 2),
      view = _useState4[0],
      setView = _useState4[1];

  var _useState5 = useState(),
      _useState6 = _slicedToArray(_useState5, 2),
      state = _useState6[0],
      setState = _useState6[1];

  var defaultLightThemeOption = EditorView.theme({
    '&': {
      backgroundColor: '#fff'
    }
  }, {
    dark: false
  });
  var defaultThemeOption = EditorView.theme({
    '&': {
      height: height,
      minHeight: minHeight,
      maxHeight: maxHeight,
      width: width,
      minWidth: minWidth,
      maxWidth: maxWidth
    }
  });
  var updateListener = EditorView.updateListener.of(function (vu) {
    if (vu.docChanged && typeof onChange === 'function' && // Fix echoing of the remote changes:
    // If transaction is market as remote we don't have to call `onChange` handler again
    !vu.transactions.some(function (tr) {
      return tr.annotation(External);
    })) {
      var doc = vu.state.doc;

      var _value = doc.toString();

      onChange(_value, vu);
    }

    onStatistics && onStatistics(getStatistics(vu));
  });
  var getExtensions = [updateListener, defaultThemeOption];

  if (defaultIndentWithTab) {
    getExtensions.unshift(keymap.of([indentWithTab]));
  }

  if (defaultBasicSetup) {
    if (typeof defaultBasicSetup === 'boolean') {
      getExtensions.unshift(basicSetup());
    } else {
      getExtensions.unshift(basicSetup(defaultBasicSetup));
    }
  }

  if (placeholderStr) {
    getExtensions.unshift(placeholder(placeholderStr));
  }

  switch (theme) {
    case 'light':
      getExtensions.push(defaultLightThemeOption);
      break;

    case 'dark':
      getExtensions.push(oneDark);
      break;

    case 'none':
      break;

    default:
      getExtensions.push(theme);
      break;
  }

  if (editable === false) {
    getExtensions.push(EditorView.editable.of(false));
  }

  if (readOnly) {
    getExtensions.push(EditorState.readOnly.of(true));
  }

  if (onUpdate && typeof onUpdate === 'function') {
    getExtensions.push(EditorView.updateListener.of(onUpdate));
  }

  getExtensions = getExtensions.concat(extensions);
  useEffect(function () {
    if (container && !state) {
      var config = {
        doc: value,
        selection: selection,
        extensions: getExtensions
      };
      var stateCurrent = initialState ? EditorState.fromJSON(initialState.json, config, initialState.fields) : EditorState.create(config);
      setState(stateCurrent);

      if (!view) {
        var viewCurrent = new EditorView({
          state: stateCurrent,
          parent: container,
          root: root
        });
        setView(viewCurrent);
        onCreateEditor && onCreateEditor(viewCurrent, stateCurrent);
      }
    }

    return function () {
      if (view) {
        setState(undefined);
        setView(undefined);
      }
    };
  }, [container, state]);
  useEffect(function () {
    return setContainer(props.container);
  }, [props.container]);
  useEffect(function () {
    return function () {
      if (view) {
        view.destroy();
        setView(undefined);
      }
    };
  }, [view]);
  useEffect(function () {
    if (autoFocus && view) {
      view.focus();
    }
  }, [autoFocus, view]);
  useEffect(function () {
    if (view) {
      view.dispatch({
        effects: StateEffect.reconfigure.of(getExtensions)
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [theme, extensions, height, minHeight, maxHeight, width, minWidth, maxWidth, placeholderStr, editable, readOnly, defaultIndentWithTab, defaultBasicSetup, onChange, onUpdate]);
  useEffect(function () {
    if (value === undefined) {
      return;
    }

    var currentValue = view ? view.state.doc.toString() : '';

    if (view && value !== currentValue) {
      view.dispatch({
        changes: {
          from: 0,
          to: currentValue.length,
          insert: value || ''
        },
        annotations: [External.of(true)]
      });
    }
  }, [value, view]);
  return {
    state: state,
    setState: setState,
    view: view,
    setView: setView,
    container: container,
    setContainer: setContainer
  };
}